import { defineComponent as _defineComponent } from 'vue'
import { Activity } from 'farmdok-rest-api';
import Vue, { computed, ref, watch, withDefaults } from 'vue';
import { useRouter } from 'vue-router/composables';

import InitI18n from '@/initI18n';

import TheContentbar from '@/layout/components/TheContentbar.vue';
import CompanySwitch from '@/shared/components/CompanySwitch.vue';
import DropdownPageOptions from '@/shared/components/DropdownPageOptions.vue';
import ProcessOrderSwitch from '@/shared/components/ProcessOrderSwitch.vue';
import SearchBar from '@/shared/components/SearchBar.vue';
import Button from '@/shared/components/buttons/Button.vue';
import ButtonDropdown from '@/shared/components/buttons/ButtonDropdown.vue';
import { availableFeatures } from '@/shared/storeDynamicFeatures';
import { useStore } from '@/store';

interface Props {
  filterString: string;
  leftSectionVisible: boolean;
}

interface ActionButton {
  text: string | undefined;
  handler: () => void;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'TheContentbarContainer',
  props: {
    filterString: { default: '' },
    leftSectionVisible: { type: Boolean, default: true }
  },
  emits: ["onDeselectAll", "onInvertSelection", "onSelectAll", "onCreateButtonClick", "update:filterString", "onExportSelected", "onShowSettings", "onExportData", "onMoveButtonClick", "onDuplicateButtonClick", "onEditButtonClick"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'onDeselectAll'): void;
  (e: 'onInvertSelection'): void;
  (e: 'onSelectAll'): void;
  (e: 'onCreateButtonClick'): void;
  (e: 'update:filterString', filterString: string): string;
  (e: 'onExportSelected', activityIds: string[]): string[];
  (e: 'onShowSettings'): void;
  (e: 'onExportData'): void;
  (e: 'onMoveButtonClick'): void;
  (e: 'onDuplicateButtonClick', activityIds: string[]): string[];
  (e: 'onEditButtonClick', activityIds: string[]): string[];
}), expose: any, slots: any, attrs: any }) {

const props = __props as { filterString: string, leftSectionVisible: boolean }



const store = useStore();
const router = useRouter();
const t = InitI18n.translate;

const filterString = ref<string>(props.filterString);
const selectedActivities = computed<Activity[]>(() => store.getters['activities/selectedActivities']);
const selectedActivityIds = computed<string[]>(() => store.getters['activities/selectedActivityIds']);
const loading = computed<boolean>(
  () =>
    store.getters['activities/loading'] ||
    store.getters['activityTypes/loading'] ||
    store.getters['customers/loading'] ||
    store.getters['fields/loading'] ||
    store.getters['products/loading'] ||
    store.getters['storagePlaces/loading'] ||
    store.getters['units/loading'],
);
const rowIsSelected = computed<boolean>(() => selectedActivityIds.value.length > 0);
const editMode = computed<boolean>(() => !loading.value && rowIsSelected.value);
const deleteActivityMessage = computed<string>(() => {
  if (selectedActivityIds.value.length === 1) {
    return t('Gewählten Eintrag wirklich löschen?') ?? 'Gewählten Eintrag wirklich löschen?';
  }
  return t('Gewählte Einträge wirklich löschen?') ?? 'Gewählte Einträge wirklich löschen?';
});
const duplicateActivityTitle = computed<string>(() => {
  if (selectedActivityIds.value.length === 1) {
    return t('Maßnahme duplizieren') ?? 'Maßnahme duplizieren';
  }
  return t('Maßnahmen duplizieren') ?? 'Maßnahmen duplizieren';
});
const moveActivityTitle = computed<string>(() => {
  if (selectedActivityIds.value.length === 1) {
    return t('Maßnahme verschieben') ?? 'Maßnahme verschieben';
  }
  return t('Maßnahmen verschieben') ?? 'Maßnahmen verschieben';
});
const featureMoveActivitiesEnabled = computed<boolean>(() =>
  store.getters.currentCompaniesHaveFeatureEnabled(availableFeatures.FEATURE_MOVE_ACTIVITIES),
);
const featureActivitiesEnabled = computed<boolean>(() =>
  store.getters.currentCompaniesHaveFeatureEnabled(availableFeatures.FEATURE_ACTIVITIES),
);
const featureActivitiesReadOnly = computed<boolean>(() =>
  store.getters.currentCompaniesHaveFeatureReadonly(availableFeatures.FEATURE_ACTIVITIES),
);
const featureActivitiesExportEnabled = computed<boolean>(() =>
  store.getters.currentCompaniesHaveFeatureEnabled(availableFeatures.FEATURE_ACTIVITIES_EXPORT),
);
const featureCreateActivityEnabled = computed<boolean>(
  () => featureActivitiesEnabled.value && !featureActivitiesReadOnly.value,
);
const featureDeleteActivityEnabled = computed<boolean>(
  () => featureActivitiesEnabled.value && !featureActivitiesReadOnly.value,
);
const featureDuplicateActivityEnabled = computed<boolean>(
  () => featureActivitiesEnabled.value && !featureActivitiesReadOnly.value,
);

const buttonUpdateSelection = ref<ActionButton[]>([
  {
    text: t('Auswahl aufheben'),
    handler: () => emit('onDeselectAll'),
  },
  {
    text: t('Auswahl umkehren'),
    handler: () => emit('onInvertSelection'),
  },
  {
    text: t('Alle auswählen'),
    handler: () => emit('onSelectAll'),
  },
]);

watch<string>(filterString, () => emit('update:filterString', filterString.value));


function onCreateButtonClick() {
  emit('onCreateButtonClick');
}

function onDeselectAll() {
  emit('onDeselectAll');
}

async function onDeleteSelected() {
  const response = await Vue.prototype.$confirm({
    message: deleteActivityMessage.value,
    okTitle: t('Löschen') ?? 'Löschen',
    okVariant: 'danger',
  });
  if (!response) {
    return;
  }
  store.dispatch('activities/delete', selectedActivities.value);
  onDeselectAll();
}

function onExportSelected(): void {
  emit('onExportSelected', selectedActivityIds.value);
}

function restoreActivities(): void {
  router.push({ name: 'activities/restore' });
}

function onShowSettings(): void {
  emit('onShowSettings');
}

function onExportData(): void {
  emit('onExportData');
}

function onDuplicateSelected(): void {
  emit('onDuplicateButtonClick', selectedActivityIds.value);
}

function onMoveSelected(): void {
  emit('onMoveButtonClick');
}

function onEditSelected(): void {
  emit('onEditButtonClick', selectedActivityIds.value);
}

return { __sfc: true,props, store, router, t, filterString, selectedActivities, selectedActivityIds, loading, rowIsSelected, editMode, deleteActivityMessage, duplicateActivityTitle, moveActivityTitle, featureMoveActivitiesEnabled, featureActivitiesEnabled, featureActivitiesReadOnly, featureActivitiesExportEnabled, featureCreateActivityEnabled, featureDeleteActivityEnabled, featureDuplicateActivityEnabled, buttonUpdateSelection, emit, onCreateButtonClick, onDeselectAll, onDeleteSelected, onExportSelected, restoreActivities, onShowSettings, onExportData, onDuplicateSelected, onMoveSelected, onEditSelected, TheContentbar, CompanySwitch, DropdownPageOptions, ProcessOrderSwitch, SearchBar, Button, ButtonDropdown }
}

})